import React from 'react';
import { getRawDateString } from '../../authConfig';
import { 
  FaSearch, 
  FaUser, 
  FaPlus, 
  FaFileDownload, 
  FaSync, 
  FaFilter, 
  FaCalendarAlt, 
  FaListOl 
} from 'react-icons/fa';

const TaxPortalToolbar = ({
  searchPlaceholder = "Search clients, IDs, or details...",
  searchText,
  onSearchChange,
  
  statusOptions,
  filterStatus,
  onFilterStatusChange,
  
  assignedToOptions,
  filterAssignedTo,
  onFilterAssignedToChange,
  
  dueDateOptions,
  filterDueDate,
  onFilterDueDateChange,
  
  pageSize,
  onPageSizeChange,
  
  onAdd,
  addLabel = "Add Record",
  
  onExport,
  disableExport = false,
  
  onRefresh,
  isRefreshing = false,
  theme = 'default'
}) => {
  const isGlass = theme === 'glass';
  
  return (
    <div className={`card mb-4 border-0 rounded-4 shadow-sm ${isGlass ? 'glass-card' : 'bg-white'}`}>
      
      {/* --- TOP SECTION: Search & Primary Actions --- */}
      <div className="p-4 pb-3">
        <div className="row g-3 align-items-center justify-content-between">
          
          {/* Main Search */}
          <div className="col-12 col-md-6 col-lg-5">
            <div className={`input-group input-group-lg rounded-3 overflow-hidden ${isGlass ? 'glass-input border-0' : 'border'}`}>
              <span className={`input-group-text border-0 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}>
                <FaSearch size={16} />
              </span>
              <input
                type="text"
                className={`form-control border-0 px-2 shadow-none fs-6 ${isGlass ? 'bg-transparent text-dark' : 'bg-white'}`}
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="col-12 col-md-auto">
            <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
              {onAdd && (
                <button className="btn btn-primary rounded-3 px-4 fw-medium d-flex align-items-center gap-2" onClick={onAdd}>
                  <FaPlus size={14} /> {addLabel}
                </button>
              )}
              {onExport && (
                <button className="btn btn-outline-success rounded-3 px-3 fw-medium d-flex align-items-center gap-2" onClick={onExport} disabled={disableExport}>
                  <FaFileDownload size={14} /> Export
                </button>
              )}
              {onRefresh && (
                <button className="btn btn-light border rounded-3 px-3 fw-medium d-flex align-items-center gap-2 text-secondary hover-primary" onClick={onRefresh} disabled={isRefreshing}>
                  <FaSync size={14} className={isRefreshing ? "fa-spin" : ""} /> {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* --- BOTTOM SECTION: Refinement Filters --- */}
      <div className={`px-4 py-3 border-top ${isGlass ? 'bg-transparent' : 'bg-light rounded-bottom-4'}`}>
        <div className="d-flex align-items-center mb-2 gap-2 text-muted" style={{ fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <FaFilter size={12} /> Refine Results
        </div>
        
        <div className="row g-3">
          {/* Status Filter */}
          {statusOptions && (
            <div className="col-12 col-sm-6 col-md-3">
              <div className={`input-group input-group-sm rounded-3 overflow-hidden ${isGlass ? 'glass-input border-0' : 'border bg-white'}`}>
                <span className={`input-group-text border-0 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaFilter size={13} /></span>
                <select 
                  className={`form-select border-0 shadow-none ${isGlass ? 'bg-transparent text-dark' : 'bg-white'}`} 
                  value={filterStatus} 
                  onChange={(e) => onFilterStatusChange(e.target.value)} 
                  style={{ cursor: 'pointer' }}
                >
                  <option value="nonCompleted">Open / Incomplete</option>
                  <option value="">All Statuses</option>
                  <hr />
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Assignee Filter */}
          {assignedToOptions && (
            <div className="col-12 col-sm-6 col-md-3">
              <div className={`input-group input-group-sm rounded-3 overflow-hidden ${isGlass ? 'glass-input border-0' : 'border bg-white'}`}>
                <span className={`input-group-text border-0 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaUser size={13} /></span>
                <select
                  className={`form-select border-0 shadow-none ${isGlass ? 'bg-transparent text-dark' : 'bg-white'}`}
                  value={filterAssignedTo}
                  onChange={(e) => onFilterAssignedToChange(e.target.value)}
                  style={{ cursor: 'pointer' }}
                >
                  <option value="">All Assignees</option>
                  {assignedToOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Due Date Filter */}
          {dueDateOptions && (
            <div className="col-12 col-sm-6 col-md-3">
              <div className={`input-group input-group-sm rounded-3 overflow-hidden ${isGlass ? 'glass-input border-0' : 'border bg-white'}`}>
                <span className={`input-group-text border-0 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaCalendarAlt size={13} /></span>
                <select 
                  className={`form-select border-0 shadow-none ${isGlass ? 'bg-transparent text-dark' : 'bg-white'}`} 
                  value={filterDueDate} 
                  onChange={(e) => onFilterDueDateChange(e.target.value)} 
                  style={{ cursor: 'pointer' }}
                >
                  <option value="">Any Due Date</option>
                  {dueDateOptions.map((date) => (
                    <option key={date} value={date}>{getRawDateString(date)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Page Size Filter */}
          {pageSize !== undefined && onPageSizeChange && (
            <div className="col-12 col-sm-6 col-md-3">
               <div className={`input-group input-group-sm rounded-3 overflow-hidden ${isGlass ? 'glass-input border-0' : 'border bg-white'}`}>
                <span className={`input-group-text border-0 ${isGlass ? 'bg-transparent text-dark' : 'bg-white text-muted'}`}><FaListOl size={13} /></span>
                <select 
                  className={`form-select border-0 shadow-none ${isGlass ? 'bg-transparent text-dark' : 'bg-white'}`} 
                  value={pageSize} 
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  style={{ cursor: 'pointer' }}
                >
                  <option value={25}>25 items per page</option>
                  <option value={100}>100 items per page</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
};

export default TaxPortalToolbar;